import React, { useEffect, useMemo, useState, useRef } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { useNavigate } from 'react-router-dom';
import BASE_URL, { IMAGE_DOMAIN } from '../../../config/config';
import Loader from '../../../components/Loader';
import FiltroBusqueda from '../../../components/ui/FiltroBusqueda';
import Popup from '../../../components/Popup';
import ModalPasos from '../../../components/ui/ModalPasos';
import CropperModal from '../../../components/ui/CropperModal';
import useCropper from '../../../components/ui/useCropper';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Trans, useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { cabecerasAuth, claves } from '../../../queries/cliente';
import { DEPTO_LABELS, ROL_LABELS, esStaff } from '../../../constants/usuarios';
import { CampoSelect, CampoTexto } from './user/CamposFormulario';
import { IDIOMAS } from '../../../i18n/idiomas';
import '../../../css/perfil-usuario.css';
import { normalizarTelefonoInternacional, validarTelefonoInternacional } from '../../../utils/telefono';
import {
  FiUsers, FiLock, FiUser, FiGlobe, FiSettings, FiCamera, FiRefreshCw, FiCopy,
} from 'react-icons/fi';
import ModalResponsables from './user/ModalResponsables';
import Paginacion from '../../../components/ui/Paginacion';

/** Cuenta nueva en blanco. Los ajustes traen los mismos valores por defecto
 *  que aplica el servidor si no se mandan. */
const FORMULARIO_VACIO = {
  usuario: '@',
  contrasena: '',
  nombre: '',
  apellidos: '',
  telefono: '',
  email: '',
  webs_del_cliente: '',
  role: 'user',
  idioma: 'es',
  emails_cc: '',
  dpto_tecnico: 'no_asignado',
  estado: 'verificado',
};

const toggleArrayValue = (arr, value, setter) => {
  setter(arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]);
};

const GestorUsuarios = () => {
    const { t, i18n } = useTranslation();
    const [showSidebar, setShowSidebar] = useState(false);
  const queryClient = useQueryClient();
  const [usuariosFiltrados, setUsuariosFiltrados] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [rolesSeleccionados, setRolesSeleccionados] = useState([]);
  const [estadosSeleccionados, setEstadosSeleccionados] = useState([]);
  const [deptosSeleccionados, setDeptosSeleccionados] = useState([]);
  const [paginaActual, setPaginaActual] = useState(1);
  // Jerarquía de clientes: la asigna el soporte desde la propia lista
  const [usuarioJerarquia, setUsuarioJerarquia] = useState(null);
  const [usuariosPorPagina, setUsuariosPorPagina] = useState(12);

  const [seleccionados, setSeleccionados] = useState(new Set());

  const token = localStorage.getItem('AuthToken');
  const navigate = useNavigate();
  const usuarioActual = token ? jwtDecode(token) : null;
  const usuarioRole = usuarioActual?.role || null;
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(FORMULARIO_VACIO);
  const [fotoFile, setFotoFile] = useState(null);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [createdResult, setCreatedResult] = useState(null);
  const [repetirContrasena, setRepetirContrasena] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const fotoInputRef = useRef(null);

  const { rawImageSrc, showCropper, crop, setCrop, zoom, setZoom,
    onCropComplete, openCropper, cancelCrop, applyCrop } = useCropper({
    onCropDone: ({ blob, previewUrl }) => {
      // Revocar preview anterior
      if (fotoPreview) { try { URL.revokeObjectURL(fotoPreview); } catch { /* noop */ } }
      setFotoFile(blob);
      setFotoPreview(previewUrl);
    }
  });

  const consultaUsuarios = useQuery({
    queryKey: claves.usuarios(),
    queryFn: async () => {
      const resp = await axios.get(`${BASE_URL}/todos-usuarios`, cabecerasAuth());
      const lista = Array.isArray(resp.data.usuarios) ? resp.data.usuarios : [];
      // Más recientes primero: el orden es parte del dato que consume la vista
      return [...lista].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    },
  });

  const usuarios = useMemo(() => consultaUsuarios.data || [], [consultaUsuarios.data]);
  const cargando = consultaUsuarios.isPending;

  const obtenerUsuarios = () => queryClient.invalidateQueries({ queryKey: claves.usuarios() });

  useEffect(() => {
    const q = busqueda.toLowerCase();
    const filtrados = usuarios.filter(user => {
      const estado = user.estado || 'verificado';
      const coincideEstado = estadosSeleccionados.length > 0
        ? estadosSeleccionados.includes(estado)
        : estado !== 'archivado';

      return (user.nombre?.toLowerCase().includes(q) ||
        user.apellidos?.toLowerCase().includes(q) ||
        user.usuario?.toLowerCase().includes(q) ||
        user.email?.toLowerCase().includes(q)) &&
      (rolesSeleccionados.length === 0 || rolesSeleccionados.includes(user.role)) &&
      coincideEstado &&
      (deptosSeleccionados.length === 0 || deptosSeleccionados.includes(user.dpto_tecnico || 'no_asignado'));
    });
    setUsuariosFiltrados(filtrados);
    setPaginaActual(1);
  }, [busqueda, rolesSeleccionados, estadosSeleccionados, deptosSeleccionados, usuarios]);

  // Conteos por categoría (sobre el total, no sobre lo ya filtrado)
  const contarPorValor = (obtenerValor) => {
    const counts = {};
    usuarios.forEach(u => {
      const v = obtenerValor(u);
      counts[v] = (counts[v] || 0) + 1;
    });
    return counts;
  };
  const conteoRoles = contarPorValor(u => u.role);
  const conteoEstados = contarPorValor(u => u.estado || 'verificado');
  const conteoDeptos = contarPorValor(u => u.dpto_tecnico || 'no_asignado');

  // Cerrar sidebar con Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSidebar) {
        setShowSidebar(false);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [showSidebar]);

  // Limpiar todos los filtros del panel de usuarios
  const limpiarFiltros = () => {
    setBusqueda('');
    setRolesSeleccionados([]);
    setEstadosSeleccionados([]);
    setDeptosSeleccionados([]);
    setPaginaActual(1);
  };

  // Selección múltiple / acciones masivas
  const toggleSeleccionado = (usuario) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (next.has(usuario)) next.delete(usuario);
      else next.add(usuario);
      return next;
    });
  };

  const todosPaginaSeleccionados = usuariosActuales => usuariosActuales.length > 0 && usuariosActuales.every(u => seleccionados.has(u.usuario));

  const toggleSeleccionarTodos = (usuariosActuales) => {
    setSeleccionados(prev => {
      const next = new Set(prev);
      if (todosPaginaSeleccionados(usuariosActuales)) {
        usuariosActuales.forEach(u => next.delete(u.usuario));
      } else {
        usuariosActuales.forEach(u => next.add(u.usuario));
      }
      return next;
    });
  };

  const mutacionCambioMasivo = useMutation({
    mutationFn: async ({ campo, valor, handles }) => {
      await Promise.all(handles.map((usuarioHandle) => {
        const u = usuarios.find((x) => x.usuario === usuarioHandle);
        if (!u) return Promise.resolve();
        return axios.put(`${BASE_URL}/usuario/update/${usuarioHandle}`, { ...u, [campo]: valor }, cabecerasAuth());
      }));
      return handles.length;
    },
    onSuccess: (total) => {
      toast.success(t('gestorUsuarios.usuariosActualizados', { count: total }));
      setSeleccionados(new Set());
    },
    onError: (error) => {
      console.error('Error en acción masiva:', error);
      toast.error(t('gestorUsuarios.errorAccionMasiva'));
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: claves.usuarios() });
    },
  });

  const procesandoBulk = mutacionCambioMasivo.isPending;

  const aplicarCambioMasivo = (campo, valor) => {
    if (seleccionados.size === 0 || !valor) return;
    mutacionCambioMasivo.mutate({ campo, valor, handles: Array.from(seleccionados) });
  };

  const exportarCSV = () => {
    const encabezados = ['usuario', 'nombre', 'apellidos', 'email', 'telefono', 'role', 'estado', 'createdAt'];
    const filas = usuariosFiltrados.map(u => encabezados.map(campo => {
      const valor = u[campo] ?? '';
      return `"${String(valor).replace(/"/g, '""')}"`;
    }).join(','));
    const csv = [encabezados.join(','), ...filas].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usuarios_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Manejo del formulario de creación
  const abrirCrear = () => {
    setForm(FORMULARIO_VACIO);
    // revoke any previous preview
    if (fotoPreview) {
      try { URL.revokeObjectURL(fotoPreview); } catch (err) { /* ignore */ }
    }
    setFotoFile(null);
    setFotoPreview(null);
    setShowCreatePopup(true);
  };

  const handleFormChange = (field, value) => {
    // For usuario field, ensure it always starts with '@'
    if (field === 'usuario') {
      let v = value || '';
      // Remove leading spaces
      v = v.replace(/^\s+/, '');
      // Remove any @ characters typed by the user to avoid duplicates
      const cleaned = v.replace(/@/g, '');
      const final = cleaned === '' ? '@' : '@' + cleaned;
      setForm(prev => ({ ...prev, usuario: final }));
      return;
    }
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Avisos en el propio campo: con toasts al enviar, el usuario no sabe cuál falla
  const ES_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const emailInvalido = Boolean(form.email) && !ES_EMAIL.test(form.email.trim());

  const correosCcInvalidos = (form.emails_cc || '')
    .split(',')
    .map((correo) => correo.trim())
    .filter((correo) => correo !== '' && !ES_EMAIL.test(correo));

  const handleFileChange = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) openCropper(f);
    e.target.value = '';
  };

  // Cerrar y limpiar el popup de creación
  const cerrarCrear = () => {
    if (fotoPreview) { try { URL.revokeObjectURL(fotoPreview); } catch { /* noop */ } }
    setFotoFile(null);
    setFotoPreview(null);
    setRepetirContrasena('');
    setShowPassword(false);
    setShowRepeatPassword(false);
    setForm(FORMULARIO_VACIO);
    setShowCreatePopup(false);
  };

  // Cerrar y limpiar el popup de confirmación
  const cerrarConfirm = () => {
    setCreatedResult(null);
    setShowConfirmPopup(false);
  };

  // Revocar preview al desmontar o cuando cambie para evitar fugas de memoria
  useEffect(() => {
    return () => {
      if (fotoPreview) {
        try { URL.revokeObjectURL(fotoPreview); } catch (err) { /* ignore */ }
      }
    };
  }, [fotoPreview]);

  const submitCrearUsuario = async () => {
    // Los campos ya los ha validado cada paso del asistente; aquí solo queda
    // normalizar el teléfono, que es lo que viaja al servidor
    const telefono = validarTelefonoInternacional(form.telefono, { obligatorio: true });
    if (!telefono.valido) {
      toast.error(t('perfilUsuario.errorTelefonoInvalido'));
      return;
    }

    // La foto de perfil es opcional

    setCreating(true);
    try {
      const fd = new FormData();
      const cleanedUsuario = (form.usuario || '').toString().trim().replace(/^@+/, '');
      if (!cleanedUsuario || cleanedUsuario.length < 1) {
        toast.error(t('gestorUsuarios.usuarioObligatorio'));
        setCreating(false);
        return;
      }
      fd.append('usuario', cleanedUsuario);
      if (form.contrasena) fd.append('contrasena', form.contrasena);
      fd.append('nombre', form.nombre);
      fd.append('apellidos', form.apellidos);
      fd.append('telefono', telefono.numero);
      fd.append('email', form.email);
      fd.append('webs_del_cliente', form.webs_del_cliente);
      fd.append('role', form.role);
      fd.append('idioma', form.idioma);
      fd.append('estado', form.estado);
      fd.append('emails_cc', form.emails_cc || '');
      if (esStaff(form.role)) fd.append('dpto_tecnico', form.dpto_tecnico);
      if (fotoFile) fd.append('fotoPerfil', fotoFile);

      const resp = await axios.post(`${BASE_URL}/crear-usuario`, fd, {
        headers: { Authorization: token, 'Content-Type': 'multipart/form-data' }
      });

      if (resp.data && resp.data.ok) {
        // displayPassword: prefer the tempPassword returned by backend, otherwise use the password that was in the form
        const displayPassword = resp.data.tempPassword || form.contrasena || '';
        setCreatedResult({ ...resp.data, displayPassword });
        // cerrar y limpiar el formulario de creación
        cerrarCrear();
        setShowConfirmPopup(true);
        toast.success(t('gestorUsuarios.usuarioCreadoOk'));
        // refrescar lista
        await obtenerUsuarios();
      } else {
        toast.error(resp.data && resp.data.message ? resp.data.message : t('gestorUsuarios.errorCrearUsuario'));
      }
    } catch (error) {
      console.error('Error al crear usuario:', error);
      const msg = error.response?.data?.message || t('gestorUsuarios.errorCrearUsuario');
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const copiarTextoBienvenida = async (texto) => {
    try {
      await navigator.clipboard.writeText(texto);
      toast.success(t('gestorUsuarios.textoCopiado'));
    } catch (e) {
      console.error('No se pudo copiar:', e);
      toast.error(t('gestorUsuarios.errorCopiarTexto'));
    }
  };

  const generarContrasena = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+-=[]{}|;:,.<>?';
    // getRandomValues y no Math.random: esto es una credencial, y Math.random
    // no es criptográficamente seguro
    const bytes = new Uint32Array(16);
    crypto.getRandomValues(bytes);
    const pw = Array.from(bytes, (n) => chars[n % chars.length]).join('');
    setForm(prev => ({ ...prev, contrasena: pw }));
    setRepetirContrasena(pw);
    setShowPassword(true);
    toast.info(t('gestorUsuarios.contrasenaGenerada'));
  };

  const copiarContrasena = async () => {
    if (!form.contrasena) return;
    try {
      await navigator.clipboard.writeText(form.contrasena);
      toast.success(t('comun.copiado'));
    } catch (err) {
      toast.error(t('comun.errorCopiar'));
    }
  };

  // ── Alta de usuario, paso a paso ──────────────────────────────────────
  // Cada paso valida lo suyo: así el error sale junto a lo que se acaba de
  // rellenar y no al final, cuando ya no se ve el campo que falla.
  const campoVacio = (valor) => !valor || String(valor).trim() === '';

  const pasosAlta = [
    {
      clave: 'identidad',
      titulo: t('gestorUsuarios.pasoIdentidad'),
      descripcion: t('gestorUsuarios.pasoIdentidadDesc'),
      validar: () => {
        if (campoVacio(form.usuario.replace(/^@+/, ''))) return t('gestorUsuarios.usuarioObligatorio');
        if (campoVacio(form.nombre) || campoVacio(form.apellidos)) return t('gestorUsuarios.camposObligatorios');
        if (campoVacio(form.email) || emailInvalido) return t('gestorUsuarios.emailInvalido');
        if (!validarTelefonoInternacional(form.telefono, { obligatorio: true }).valido) {
          return t('perfilUsuario.errorTelefonoInvalido');
        }
        return null;
      },
      contenido: (
        <div className="fcu-campos">
          <CampoTexto
            id="cu-usuario"
            label={`${t('gestorUsuarios.usuarioLogin')} *`}
            hint={t('gestorUsuarios.hintUsuario')}
            value={form.usuario}
            onChange={e => handleFormChange('usuario', e.target.value)}
            placeholder="@johndoe"
          />

          <div className="fcu-grid">
            <CampoTexto
              id="cu-nombre"
              label={`${t('gestorUsuarios.nombre')} *`}
              value={form.nombre}
              onChange={e => handleFormChange('nombre', e.target.value)}
              placeholder={t('gestorUsuarios.placeholderNombre')}
            />
            <CampoTexto
              id="cu-apellidos"
              label={`${t('gestorUsuarios.apellidos')} *`}
              value={form.apellidos}
              onChange={e => handleFormChange('apellidos', e.target.value)}
              placeholder={t('gestorUsuarios.placeholderApellidos')}
            />
            <CampoTexto
              id="cu-email"
              type="email"
              label={`${t('gestorUsuarios.email')} *`}
              value={form.email}
              onChange={e => handleFormChange('email', e.target.value)}
              placeholder={t('gestorUsuarios.placeholderEmail')}
              error={emailInvalido ? t('gestorUsuarios.emailInvalido') : undefined}
            />
            <CampoTexto
              id="cu-telefono"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              label={`${t('gestorUsuarios.telefono')} *`}
              value={form.telefono}
              onChange={e => handleFormChange('telefono', e.target.value)}
              onBlur={() => handleFormChange('telefono', normalizarTelefonoInternacional(form.telefono))}
              placeholder={t('perfilUsuario.telefonoInternacionalPlaceholder')}
            />
          </div>

          <div className="pu-campo">
            <label>{t('gestorUsuarios.fotoDePerfil')} <span className="fcu-opcional">{t('gestorUsuarios.opcional')}</span></label>
            <input ref={fotoInputRef} type="file" accept="image/*" onChange={handleFileChange} style={{ display: 'none' }} />
            <div className="foto-perfil-uploader">
              {fotoPreview ? (
                <div className="foto-preview-wrap">
                  <img src={fotoPreview} alt={t('gestorUsuarios.vistaPrevia')} className="foto-preview-circle" />
                  <div className="foto-preview-actions">
                    <button type="button" className="btn-foto-change" onClick={() => fotoInputRef.current?.click()}>
                      {t('gestorUsuarios.cambiarFoto')}
                    </button>
                    <button type="button" className="btn-foto-remove" onClick={() => { setFotoFile(null); setFotoPreview(null); }}>
                      {t('gestorUsuarios.quitar')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="foto-upload-placeholder" onClick={() => fotoInputRef.current?.click()}>
                  <span className="foto-upload-text">{t('gestorUsuarios.subirFoto')}</span>
                  <span className="foto-upload-hint">{t('gestorUsuarios.formatosFoto')}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      ),
    },
    {
      clave: 'cuenta',
      titulo: t('gestorUsuarios.pasoCuenta'),
      descripcion: t('gestorUsuarios.pasoCuentaDesc'),
      validar: () => {
        if (campoVacio(form.webs_del_cliente)) return t('gestorUsuarios.camposObligatorios');
        if (correosCcInvalidos.length > 0) {
          return t('perfilUsuario.correosCopiaInvalidos', { correos: correosCcInvalidos.join(', ') });
        }
        return null;
      },
      contenido: (
        <div className="fcu-campos">
            <CampoSelect
              id="cu-rol"
              label={`${t('gestorUsuarios.rol')} *`}
              hint={usuarioRole === 'tecnico' ? t('gestorUsuarios.hintTecnicoCorto') : undefined}
              value={form.role}
              onChange={e => handleFormChange('role', e.target.value)}
            >
              {['user', 'tecnico', 'admin'].map((valor) => (
                <option key={valor} value={valor} disabled={usuarioRole === 'tecnico' && valor !== 'user'}>
                  {ROL_LABELS[valor]}
                </option>
              ))}
            </CampoSelect>

            <CampoSelect
              id="cu-estado"
              label={t('perfilUsuario.estadoCuenta')}
              hint={t('gestorUsuarios.hintEstado')}
              value={form.estado}
              onChange={e => handleFormChange('estado', e.target.value)}
            >
              <option value="verificado">{t('perfilUsuario.verificadoOpcion')}</option>
              <option value="verificar">{t('perfilUsuario.verificarOpcion')}</option>
              <option value="suspendido">{t('perfilUsuario.suspendidoOpcion')}</option>
            </CampoSelect>

            {/* Un <select> y no SelectorIdioma: ese cambia el idioma de quien
                está usando el panel, y aquí se elige el del usuario nuevo */}
            <CampoSelect
              id="cu-idioma"
              label={t('gestorUsuarios.idiomaUsuario')}
              hint={t('gestorUsuarios.hintIdioma')}
              value={form.idioma}
              onChange={e => handleFormChange('idioma', e.target.value)}
            >
              {IDIOMAS.map((idioma) => (
                <option key={idioma.codigo} value={idioma.codigo}>{idioma.nombre}</option>
              ))}
            </CampoSelect>

            {/* Solo el staff atiende un departamento */}
            {esStaff(form.role) && (
              <CampoSelect
                id="cu-dpto"
                label={t('perfilUsuario.departamentoTecnico')}
                value={form.dpto_tecnico}
                onChange={e => handleFormChange('dpto_tecnico', e.target.value)}
              >
                {Object.entries(DEPTO_LABELS).map(([valor, label]) => (
                  <option key={valor} value={valor}>{label}</option>
                ))}
              </CampoSelect>
            )}

          <CampoTexto
            id="cu-webs"
            label={`${t('gestorUsuarios.webCliente')} *`}
            hint={t('gestorUsuarios.hintWebs')}
            value={form.webs_del_cliente}
            onChange={e => handleFormChange('webs_del_cliente', e.target.value)}
            placeholder={t('gestorUsuarios.placeholderWeb')}
          />

          <CampoTexto
            id="cu-emails-cc"
            label={t('perfilUsuario.correosCopia')}
            hint={t('gestorUsuarios.hintCorreosCopia')}
            value={form.emails_cc}
            onChange={e => handleFormChange('emails_cc', e.target.value)}
            placeholder={t('perfilUsuario.correosCopiaPlaceholder')}
            error={correosCcInvalidos.length > 0
              ? t('perfilUsuario.correosCopiaInvalidos', { correos: correosCcInvalidos.join(', ') })
              : undefined}
          />
        </div>
      ),
    },
    {
      clave: 'acceso',
      titulo: t('gestorUsuarios.pasoAcceso'),
      descripcion: t('gestorUsuarios.pasoAccesoDesc'),
      validar: () => {
        if (campoVacio(form.contrasena)) return t('gestorUsuarios.contrasenaObligatoria');
        if (form.contrasena !== repetirContrasena) return t('gestorUsuarios.contrasenasNoCoinciden');
        return null;
      },
      contenido: (
        <div className="fcu-campos">
          <div className="pu-campo">
            <label htmlFor="cu-contrasena">{t('gestorUsuarios.contrasena')} *</label>
            <div className="fcu-password">
              <input
                id="cu-contrasena"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('gestorUsuarios.placeholderContrasena')}
                value={form.contrasena}
                onChange={e => handleFormChange('contrasena', e.target.value)}
              />
              <button
                type="button"
                className="fcu-icono"
                onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? t('gestorUsuarios.ocultarContrasena') : t('gestorUsuarios.mostrarContrasena')}
              >
                <img className="icono-ojo" src={showPassword ? '/images/ojo-abierto.svg' : '/images/ojo-cerrado.svg'} alt="" />
              </button>
              <button type="button" className="fcu-icono" onClick={generarContrasena} title={t('gestorUsuarios.generarContrasena')} aria-label={t('gestorUsuarios.generarContrasena')}>
                <FiRefreshCw />
              </button>
              <button type="button" className="fcu-icono" onClick={copiarContrasena} disabled={!form.contrasena} title={t('comun.copiar')} aria-label={t('comun.copiar')}>
                <FiCopy />
              </button>
            </div>
          </div>

          <div className="pu-campo">
            <label htmlFor="cu-repetir">{t('gestorUsuarios.repetirContrasena')} *</label>
            <div className="fcu-password">
              <input
                id="cu-repetir"
                type={showRepeatPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder={t('gestorUsuarios.placeholderRepetir')}
                value={repetirContrasena}
                onChange={e => setRepetirContrasena(e.target.value)}
                className={repetirContrasena && repetirContrasena !== form.contrasena ? 'input-error' : ''}
              />
              <button
                type="button"
                className="fcu-icono"
                onClick={() => setShowRepeatPassword(v => !v)}
                aria-label={showRepeatPassword ? t('gestorUsuarios.ocultarRepetir') : t('gestorUsuarios.mostrarRepetir')}
              >
                <img className="icono-ojo" src={showRepeatPassword ? '/images/ojo-abierto.svg' : '/images/ojo-cerrado.svg'} alt="" />
              </button>
            </div>
            {repetirContrasena && repetirContrasena !== form.contrasena && (
              <p className="pu-campo-error">{t('gestorUsuarios.contrasenasNoCoinciden')}</p>
            )}
          </div>

          {/* Repaso antes de crear: es el último punto donde se puede corregir */}
          <div className="fcu-resumen">
            <h5>{t('gestorUsuarios.resumen')}</h5>
            <dl>
              <div><dt>{t('gestorUsuarios.usuarioLogin')}</dt><dd>{form.usuario}</dd></div>
              <div><dt>{t('gestorUsuarios.nombre')}</dt><dd>{form.nombre} {form.apellidos}</dd></div>
              <div><dt>{t('gestorUsuarios.email')}</dt><dd>{form.email}</dd></div>
              <div><dt>{t('gestorUsuarios.rol')}</dt><dd>{ROL_LABELS[form.role]}</dd></div>
              {esStaff(form.role) && (
                <div><dt>{t('perfilUsuario.departamentoTecnico')}</dt><dd>{DEPTO_LABELS[form.dpto_tecnico]}</dd></div>
              )}
              <div><dt>{t('gestorUsuarios.idiomaUsuario')}</dt><dd>{IDIOMAS.find(i => i.codigo === form.idioma)?.nombre}</dd></div>
              <div><dt>{t('perfilUsuario.estadoCuenta')}</dt><dd>{t(`perfilUsuario.${form.estado}Opcion`)}</dd></div>
            </dl>
          </div>
        </div>
      ),
    },
  ];

  const indiceUltimo = paginaActual * usuariosPorPagina;
  const indicePrimero = indiceUltimo - usuariosPorPagina;
  const usuariosActuales = usuariosFiltrados.slice(indicePrimero, indiceUltimo);

  const irADetalle = (usuario) => {
    try {
      navigate(`/dashboard/usuarios/${usuario}`);
    } catch (error) {
      console.error('Error al navegar:', error);
    }
  };

  if (cargando) {
    return <div className="loader-fullscreen"><Loader /></div>;
  }

  return (
    <main className="site-width">
      {usuarioJerarquia && (
        <ModalResponsables
          usuario={usuarioJerarquia}
          usuarios={usuarios}
          onCerrar={() => setUsuarioJerarquia(null)}
          onGuardado={(datos) => {
            toast.success(t('gestorUsuarios.supervisadosGuardados', { count: datos.hijos.length }));
            if (datos.descartados?.length > 0) {
              toast.info(t('gestorUsuarios.responsablesDescartados', { count: datos.descartados.length }));
            }
          }}
        />
      )}

      <ToastContainer position="top-right" autoClose={3000} />
      <div className="contenedor-gestor-usuarios">
        <div className="pagina-cabecera">
          <h2>{t('gestorUsuarios.titulo')}</h2>
          <p>{t('gestorUsuarios.subtitulo')}</p>
        </div>

        <div className="filtro-tickets-admin">
          <input
            type="text"
            placeholder={t('gestorUsuarios.buscarUsuario')}
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
          />
          <div className="filtros-acciones">
            <select
              className="selector-page-size"
              value={usuariosPorPagina}
              onChange={e => { setUsuariosPorPagina(Number(e.target.value)); setPaginaActual(1); }}
              title={t('gestorUsuarios.usuariosPorPagina')}
            >
              <option value={12}>{t('gestorUsuarios.porPagina', { n: 12 })}</option>
              <option value={24}>{t('gestorUsuarios.porPagina', { n: 24 })}</option>
              <option value={50}>{t('gestorUsuarios.porPagina', { n: 50 })}</option>
            </select>
            <button type="button" className="chip-toggle" onClick={exportarCSV}>
              {t('gestorUsuarios.exportarCsv')}
            </button>
            <button
              className="boton-toggle-filtros"
              onClick={() => setShowSidebar(!showSidebar)}
            >
              {t('gestorUsuarios.filtros')}
            </button>
            <button className="boton-crear-usuario" onClick={abrirCrear}>{t('gestorUsuarios.crearUsuario')}</button>
          </div>
        </div>

        <div className={`sidebar-filtros-overlay ${showSidebar ? 'open' : ''}`} onClick={() => setShowSidebar(false)} />
        <aside className={`sidebar-filtros${showSidebar ? ' open' : ''}`}>
          <FiltroBusqueda
            title={t('gestorUsuarios.filtrarUsuarios')}
            value={busqueda}
            onChange={setBusqueda}
            placeholder={t('gestorUsuarios.buscarUsuario')}
            className="sidebar-filtros-content"
            showInput={false}
            onClose={() => setShowSidebar(false)}
            showFooter={true}
            onReset={limpiarFiltros}
            resetText={t('gestorUsuarios.resetearFiltros')}
          >
            <div className="grupo-filtro grupo-filtro-checkbox">
              <label>{t('gestorUsuarios.rol')}</label>
              {[
                { value: 'user', label: t('gestorUsuarios.roles.user') },
                { value: 'tecnico', label: t('gestorUsuarios.roles.tecnico') },
                { value: 'admin', label: t('gestorUsuarios.roles.admin') },
              ].map(opt => (
                <label key={opt.value} className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={rolesSeleccionados.includes(opt.value)}
                    onChange={() => toggleArrayValue(rolesSeleccionados, opt.value, setRolesSeleccionados)}
                  />
                  <span>{opt.label}</span>
                  <span className="checkbox-filtro-count">{conteoRoles[opt.value] || 0}</span>
                </label>
              ))}
            </div>

            <div className="grupo-filtro grupo-filtro-checkbox">
              <label>{t('gestorUsuarios.estado')}</label>
              {[
                { value: 'verificado', label: t('gestorUsuarios.estados.verificado') },
                { value: 'verificar', label: t('gestorUsuarios.estados.verificar') },
                { value: 'suspendido', label: t('gestorUsuarios.estados.suspendido') },
                { value: 'archivado', label: t('usuarios.estados.archivado') },
              ].map(opt => (
                <label key={opt.value} className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={estadosSeleccionados.includes(opt.value)}
                    onChange={() => toggleArrayValue(estadosSeleccionados, opt.value, setEstadosSeleccionados)}
                  />
                  <span>{opt.label}</span>
                  <span className="checkbox-filtro-count">{conteoEstados[opt.value] || 0}</span>
                </label>
              ))}
            </div>

            <div className="grupo-filtro grupo-filtro-checkbox">
              <label>{t('gestorUsuarios.departamento')}</label>
              {Object.entries(DEPTO_LABELS).map(([value, label]) => (
                <label key={value} className="checkbox-filtro">
                  <input
                    type="checkbox"
                    checked={deptosSeleccionados.includes(value)}
                    onChange={() => toggleArrayValue(deptosSeleccionados, value, setDeptosSeleccionados)}
                  />
                  <span>{label}</span>
                  <span className="checkbox-filtro-count">{conteoDeptos[value] || 0}</span>
                </label>
              ))}
            </div>
          </FiltroBusqueda>
        </aside>

        {seleccionados.size > 0 && (
          <div className="bulk-toolbar">
            <span className="bulk-toolbar-count">{t('gestorUsuarios.seleccionados', { count: seleccionados.size })}</span>
            <select
              disabled={procesandoBulk}
              defaultValue=""
              onChange={e => { const v = e.target.value; e.target.value = ''; if (v) aplicarCambioMasivo('estado', v); }}
            >
              <option value="" disabled>{t('gestorUsuarios.cambiarEstado')}</option>
              <option value="verificado">{t('gestorUsuarios.verificar')}</option>
              <option value="suspendido">{t('gestorUsuarios.suspender')}</option>
            </select>
            <button type="button" className="bulk-cancelar" onClick={() => setSeleccionados(new Set())} disabled={procesandoBulk}>
              {t('gestorUsuarios.cancelarSeleccion')}
            </button>
          </div>
        )}

        <div className="lista-usuarios">
          <div className="usuarios-cabecera">
            <label className="tarjeta-usuario-checkbox" onClick={(e) => e.stopPropagation()}>
              <input
                type="checkbox"
                checked={todosPaginaSeleccionados(usuariosActuales)}
                onChange={() => toggleSeleccionarTodos(usuariosActuales)}
                aria-label={t('gestorUsuarios.seleccionarTodosPagina')}
              />
            </label>
            <span className="cabecera-identidad">{t('gestorUsuarios.colUsuario')}</span>
            <span>{t('gestorUsuarios.rol')}</span>
            <span>{t('gestorUsuarios.estado')}</span>
            <span>{t('gestorUsuarios.colAlta')}</span>
            <span />
          </div>
          {usuariosActuales.length > 0 ? (
            usuariosActuales.map(user => {
              const fechaAlta = new Date(user.createdAt);
              const fechaCorta = fechaAlta.toLocaleDateString(i18n.language, {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
              });
              const fechaCompleta = `${t('gestorUsuarios.seUnioEl')}${fechaAlta.toLocaleString(i18n.language, {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}`;

              return (
                <div
                  key={user.usuario}
                  className={`tarjeta-usuario ${user.estado === 'suspendido' ? 'usuario-suspendido' : user.estado === 'archivado' ? 'usuario-archivado' : user.estado === 'verificar' ? 'usuario-pendiente' : ''}`}
                  onClick={() => irADetalle(user.usuario)}
                >
                  <div className="tarjeta-usuario-checkbox" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={seleccionados.has(user.usuario)}
                      onChange={() => toggleSeleccionado(user.usuario)}
                      aria-label={t('gestorUsuarios.seleccionarUsuario', { usuario: user.usuario })}
                    />
                  </div>

                  <div className="imagen-usuario">
                    <img src={user.fotoPerfil ? (user.fotoPerfil.startsWith('http') ? user.fotoPerfil : `${IMAGE_DOMAIN}${user.fotoPerfil}`) : `${IMAGE_DOMAIN}/images/default-user.png`} alt={t('gestorUsuarios.fotoDePerfil')} />
                  </div>

                  <div className="info-usuario">
                    <h3>{user.nombre} {user.apellidos}</h3>
                    <p className="nombre-usuario">@{user.usuario} <span className="email-usuario">· {user.email}</span></p>
                  </div>

                  <div className="celda-rol">
                    <span className={`badge-usuario ${user.role === 'admin' ? 'rol-admin' : user.role === 'tecnico' ? 'rol-tecnico' : 'rol-user'}`}>
                      {t(`gestorUsuarios.roles.${user.role}`, { defaultValue: t('gestorUsuarios.roles.user') })}
                    </span>
                  </div>

                  <div className="celda-estado">
                    {user.estado === 'verificar' && (
                      <span className="badge-usuario pendiente-verificar">{t('gestorUsuarios.requiereAtencion')}</span>
                    )}
                    {user.estado === 'suspendido' && (
                      <span className="badge-usuario estado-suspendido">{t('gestorUsuarios.estados.suspendido')}</span>
                    )}
                    {user.estado === 'archivado' && (
                      <span className="badge-usuario estado-archivado">{t('usuarios.estados.archivado')}</span>
                    )}
                  </div>

                  <p className="fecha-union" title={fechaCompleta}>{fechaCorta}</p>

                  <div className="acciones-usuario" onClick={(e) => e.stopPropagation()}>
                    {user.role === 'user' && (
                      <button
                        type="button"
                        className="boton-responsables"
                        title={t('gestorUsuarios.supervisados')}
                        onClick={() => setUsuarioJerarquia(user)}
                      >
                        <FiUsers aria-hidden="true" />
                      </button>
                    )}
                    <button onClick={() => irADetalle(user.usuario)}>{t('gestorUsuarios.gestionarPerfil')}</button>
                  </div>
                </div>
              );
            })
          ) : (
            <p className="mensaje-no-usuarios">{t('gestorUsuarios.sinUsuariosEncontrados')}</p>
          )}
        </div>

        {/* Modal de recorte (fuera del Popup para no interferir con z-index) */}
        {showCropper && (
          <CropperModal
            rawImageSrc={rawImageSrc}
            crop={crop} setCrop={setCrop}
            zoom={zoom} setZoom={setZoom}
            onCropComplete={onCropComplete}
            onCancel={cancelCrop}
            onApply={applyCrop}
          />
        )}

        {/* Alta de usuario: asistente en tres pasos */}
        <ModalPasos
          abierto={showCreatePopup}
          titulo={t('gestorUsuarios.crearNuevoUsuario')}
          pasos={pasosAlta}
          procesando={creating}
          textoFinalizar={t('gestorUsuarios.crearUsuario')}
          onFinalizar={submitCrearUsuario}
          onCerrar={cerrarCrear}
        />


        {/* Popup - Confirmación */}
        <Popup
          open={showConfirmPopup}
          title={t('gestorUsuarios.usuarioCreado')}
          onClose={cerrarConfirm}
          showConfirm={false}
          footerActions={(
            <>
              <button className="boton-aceptar-popup" onClick={() => copiarTextoBienvenida(document.getElementById('textoBienvenida').value)}>{t('gestorUsuarios.copiarTexto')}</button>
            </>
          )}
        >
          {createdResult ? (
            <div className="form-crear-usuario">
              <div className="form-field">
                <label>{t('gestorUsuarios.resumen')}</label>
                <p>{t('gestorUsuarios.usuarioCreadoResumen')}</p>
              </div>

              <div className="form-field">
                <label>{t('gestorUsuarios.textoParaEnviar')}</label>
                <textarea
                  defaultValue={(() => {
                    const nombre = createdResult.data.nombre || '';
                    const usuario = createdResult.data.usuario || '';
                    const email = createdResult.data.email || '';
                    const contrasena = createdResult.displayPassword || '';
                    return t('gestorUsuarios.textoBienvenidaUsuario', { nombre, usuario, email, contrasena });
                  })()}
                  rows={10}
                  id="textoBienvenida"
                />
              </div>
            </div>
          ) : (
            <p>{t('gestorUsuarios.usuarioCreado')}.</p>
          )}
        </Popup>
        <Paginacion
          pagina={paginaActual}
          porPagina={usuariosPorPagina}
          total={usuariosFiltrados.length}
          onCambiar={setPaginaActual}
          etiqueta={t('gestorUsuarios.paginacion')}
        />
      </div>
    </main>
  );
};

export default GestorUsuarios;
